import { Type, type Static } from 'typebox';

export const PROTOCOL_VERSION = 1 as const;
export type MissionId = 'M1' | 'M2' | 'M3';
export type AttemptStatus =
  | 'READY'
  | 'RUNNING'
  | 'PAUSED'
  | 'EVENT_LOCK'
  | 'DISCONNECTED_GRACE'
  | 'TECH_PAUSE'
  | 'COMPLETED'
  | 'ABORTED'
  | 'INCIDENT'
  | 'INSOLVENT';
export type Side = 'BUY' | 'SELL';
export type OrderType = 'MKT' | 'LMT';
export type TimeInForce = 'DAY' | 'GTC' | 'IOC' | 'FOK' | 'GTD';
export type OrderStatus =
  | 'IN_TRANSMISSION'
  | 'ACTIVE'
  | 'PARTIALLY_FILLED'
  | 'CANCEL_PENDING'
  | 'FILLED'
  | 'CANCELED'
  | 'EXPIRED'
  | 'REJECTED'
  | 'CANCELED_END';
export type FeedCategory = 'DESK' | 'MARKET' | 'RISK' | 'SYSTEM';

export interface ContractSpec {
  readonly symbol: string;
  readonly root: string;
  readonly description: string;
  readonly exchange: 'CME' | 'CBOT' | 'ARCA';
  readonly assetClass: 'EQUITY_ETF' | 'EQUITY_INDEX' | 'RATES';
  readonly expiry: string | null;
  readonly lastTradeDate: string | null;
  readonly tickSize: number;
  readonly tickValue: number;
  readonly multiplier: number;
  readonly initialMargin: number;
  readonly maintenanceMargin: number;
  readonly marketProtectionTicks: number;
  readonly fatFingerQty: number;
  readonly commissionPerSide: number;
  readonly betaSp500: number | null;
  readonly dv01: number | null;
  readonly quoteStyle: 'DECIMAL' | 'TREASURY_32NDS';
}

export interface BookLevel {
  readonly price: number;
  readonly quantity: number;
  readonly ownQuantity: number;
  readonly volumeAhead: number;
}

export interface InstrumentMarket {
  readonly symbol: string;
  readonly bid: number;
  readonly ask: number;
  readonly last: number;
  readonly mid: number;
  /** Fractional return: 0.01 means +1%. */
  readonly changePct: number;
  readonly bidLevels: readonly BookLevel[];
  readonly askLevels: readonly BookLevel[];
  readonly cumulativeVolume: number;
  readonly chart: readonly ChartPoint[];
  readonly tradable: boolean;
}

export interface ChartPoint {
  readonly time: number;
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
  readonly volume: number;
}

export interface PositionView {
  readonly symbol: string;
  readonly quantity: number;
  readonly averagePrice: number;
  readonly markPrice: number;
  readonly marketValue: number;
  readonly unrealizedPnl: number;
  readonly realizedPnl: number;
}

export interface OrderView {
  readonly orderId: string;
  readonly clientOrderId: string;
  readonly symbol: string;
  readonly side: Side;
  readonly type: OrderType;
  readonly tif: TimeInForce;
  readonly limitPrice: number | null;
  readonly acceptedQty: number;
  readonly filledQty: number;
  readonly remainingQty: number;
  readonly status: OrderStatus;
  readonly submittedAt: number;
  readonly updatedAt: number;
  readonly convertedFromMarket: boolean;
  readonly autoLiquidation: boolean;
  readonly rejectReason: string | null;
}

export interface FillView {
  readonly fillId: string;
  readonly orderId: string;
  readonly symbol: string;
  readonly side: Side;
  readonly quantity: number;
  readonly price: number;
  readonly commission: number;
  readonly simulatedAt: number;
  readonly referenceMid: number;
  readonly slippageTicks: number;
  readonly autoLiquidation: boolean;
}

export interface RiskView {
  readonly nav: number;
  readonly cash: number;
  readonly unrealizedPnl: number;
  readonly realizedPnl: number;
  readonly currentDrawdown: number;
  readonly maxDrawdown: number;
  readonly grossExposure: number;
  readonly netExposure: number;
  readonly equityLeverage: number;
  readonly concentration: number;
  readonly initialMargin: number;
  readonly maintenanceMargin: number;
  readonly availableMargin: number;
  readonly marginUtilization: number;
  readonly marginCushion: number;
  readonly equityBeta: number | null;
  readonly dv01: number | null;
  readonly techExposure: number | null;
}

export interface FeedMessage {
  readonly id: string;
  readonly category: FeedCategory;
  readonly author: string | null;
  readonly role: string | null;
  readonly portrait: 'elise' | 'thomas' | 'maya' | 'julien' | null;
  readonly simulatedAt: number;
  readonly text: string;
  readonly emphasis: boolean;
  readonly action?: { readonly type: 'OPEN_FOMC_WATCH'; readonly label: string };
}

export interface ObjectiveView {
  readonly title: string;
  readonly instruction: string;
  readonly target: string;
  readonly tolerance: string | null;
  readonly deadline: number;
  readonly hardConstraints: readonly string[];
}

export interface TerminalSnapshot {
  readonly protocolVersion: typeof PROTOCOL_VERSION;
  readonly attemptId: string;
  readonly missionId: MissionId;
  readonly scenarioVersion: string;
  readonly engineVersion: string;
  readonly scoreVersion: string;
  readonly status: AttemptStatus;
  readonly simulatedAt: number;
  readonly startAt: number;
  readonly endAt: number;
  readonly sessionDate: string;
  readonly speed: 1 | 2;
  readonly connection: 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED';
  readonly eventLock: boolean;
  readonly selectedSymbol: string;
  readonly defaultWatchlist: readonly string[];
  readonly instruments: readonly ContractSpec[];
  readonly markets: Readonly<Record<string, InstrumentMarket>>;
  readonly positions: readonly PositionView[];
  readonly orders: readonly OrderView[];
  readonly fills: readonly FillView[];
  readonly messages: readonly FeedMessage[];
  readonly risk: RiskView;
  readonly objective: ObjectiveView;
  readonly fomcWatch: FomcWatchView | null;
  readonly controls: {
    readonly canPause: boolean;
    readonly canChangeSpeed: boolean;
    readonly canTrade: boolean;
    readonly canFinish: boolean;
  };
}

export interface FomcProbabilityRow {
  readonly meetingDate: string;
  readonly outcomes: readonly { readonly label: string; readonly probability: number }[];
  readonly previousOutcomes: readonly { readonly label: string; readonly probability: number }[];
}

export interface FomcWatchView {
  readonly updatedAt: number;
  readonly futures: readonly { readonly symbol: string; readonly price: number; readonly change: number }[];
  readonly meetings: readonly FomcProbabilityRow[];
}

export interface MissionSummary {
  readonly missionId: MissionId;
  readonly title: string;
  readonly hook: string;
  readonly durationLabel: string;
  readonly durationSeconds: number;
  readonly skills: readonly string[];
  readonly recommended: boolean;
  readonly played: boolean;
  readonly latestScore: number | null;
  readonly bestScore: number | null;
  readonly scenarioVersion: string;
}

export interface MissionBrief {
  readonly missionId: MissionId;
  readonly title: string;
  readonly eyebrow: string;
  readonly narrative: string;
  readonly role: string;
  readonly initialPortfolio: readonly string[];
  readonly knownMandate: readonly string[];
  readonly knownConstraints: readonly string[];
  readonly scoreDimensions: readonly { readonly label: string; readonly weight: number }[];
  readonly instruments: readonly string[];
  readonly startAt: number;
  readonly endAt: number;
}

export interface AccountView {
  readonly accountId: string;
  readonly displayName: string;
  readonly normalizedName: string;
  readonly createdAt: string;
  readonly lastOpenedAt: string;
  readonly hasActiveAttempt: boolean;
  readonly activeAttemptId: string | null;
}

export interface OpenAccountResponse {
  readonly account: AccountView;
  readonly takeoverRequired: boolean;
}

export interface MissionsResponse {
  readonly missions: readonly (MissionSummary & { readonly brief: MissionBrief })[];
}

export interface AttemptResponse {
  readonly attemptId: string;
  readonly snapshot: TerminalSnapshot;
}

export interface HistoryResponse {
  readonly history: readonly HistoryItem[];
}

export interface DebriefResponse {
  readonly debrief: DebriefView;
}

export interface HistoryItem {
  readonly attemptId: string;
  readonly missionId: MissionId;
  readonly missionTitle: string;
  readonly scenarioVersion: string;
  readonly currentVersion: boolean;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly durationSeconds: number;
  readonly status: AttemptStatus;
  readonly score: number | null;
  readonly label: string | null;
}

export interface DebriefMetric {
  readonly id: string;
  readonly label: string;
  readonly rawValue: string;
  readonly reference: string;
  readonly subscore: number;
  readonly weight: number;
  readonly contribution: number;
}

export interface DebriefView {
  readonly attemptId: string;
  readonly missionId: MissionId;
  readonly missionTitle: string;
  readonly scenarioVersion: string;
  readonly status: AttemptStatus;
  readonly score: number | null;
  readonly scoreLabel: string | null;
  readonly success: boolean;
  readonly caps: readonly { readonly value: number; readonly reason: string; readonly simulatedAt: number | null }[];
  readonly leads: readonly { readonly kind: 'POSITIVE' | 'ERROR' | 'DECISION'; readonly text: string }[];
  readonly metrics: readonly DebriefMetric[];
  readonly timeline: readonly FeedMessage[];
  readonly fills: readonly FillView[];
  readonly benchmarks: readonly { readonly label: string; readonly value: string; readonly detail: string }[];
  readonly comparison: { readonly previousScore: number | null; readonly delta: number | null };
}

export interface PlaceOrderPayload {
  readonly symbol: string;
  readonly side: Side;
  readonly type: OrderType;
  readonly quantity: number;
  readonly limitPrice: number | null;
  readonly tif: TimeInForce;
  readonly goodTillTime: number | null;
}

export type ClientCommandPayload =
  | { readonly type: 'PLACE_ORDER'; readonly order: PlaceOrderPayload }
  | { readonly type: 'REPLACE_ORDER'; readonly orderId: string; readonly quantity: number; readonly limitPrice: number | null }
  | { readonly type: 'CANCEL_ORDER'; readonly orderId: string }
  | { readonly type: 'SET_SPEED'; readonly speed: 1 | 2 }
  | { readonly type: 'PAUSE' }
  | { readonly type: 'RESUME' }
  | { readonly type: 'FINISH_M2' };

export interface ClientCommandEnvelope {
  readonly protocolVersion: typeof PROTOCOL_VERSION;
  readonly commandId: string;
  readonly attemptId: string;
  readonly clientSentAt: string;
  readonly payload: ClientCommandPayload;
}

export interface AckMessage {
  readonly type: 'ACK';
  readonly seq: number;
  readonly commandId: string;
  readonly accepted: boolean;
  readonly reason: string | null;
  readonly serverReceivedAt: string;
}

export type ServerMessage =
  | AckMessage
  | { readonly type: 'SNAPSHOT'; readonly seq: number; readonly snapshot: TerminalSnapshot }
  | { readonly type: 'STATE_DELTA'; readonly seq: number; readonly snapshot: TerminalSnapshot }
  | { readonly type: 'MARKET_BATCH'; readonly seq: number; readonly markets: TerminalSnapshot['markets']; readonly simulatedAt: number }
  | { readonly type: 'EVENT'; readonly seq: number; readonly event: FeedMessage }
  | { readonly type: 'CONTROL_REVOKED'; readonly seq: number; readonly reason: string }
  | { readonly type: 'HEARTBEAT'; readonly seq: number; readonly serverAt: string };

export const PseudoSchema = Type.String({ minLength: 3, maxLength: 24 });
export const OpenAccountBodySchema = Type.Object({ pseudo: PseudoSchema });
export type OpenAccountBody = Static<typeof OpenAccountBodySchema>;
export const CreateAttemptBodySchema = Type.Object({ missionId: Type.Union([Type.Literal('M1'), Type.Literal('M2'), Type.Literal('M3')]) });
export type CreateAttemptBody = Static<typeof CreateAttemptBodySchema>;
export const AttemptParamsSchema = Type.Object({ id: Type.String({ minLength: 1 }) });
export const AccountParamsSchema = Type.Object({ id: Type.String({ minLength: 1 }) });
export const WsQuerySchema = Type.Object({ afterSeq: Type.Optional(Type.Integer({ minimum: 0 })) });

const NullableNumberSchema = Type.Union([Type.Number(), Type.Null()]);
const SideSchema = Type.Union([Type.Literal('BUY'), Type.Literal('SELL')]);
const OrderTypeSchema = Type.Union([Type.Literal('MKT'), Type.Literal('LMT')]);
const TifSchema = Type.Union([Type.Literal('DAY'), Type.Literal('GTC'), Type.Literal('IOC'), Type.Literal('FOK'), Type.Literal('GTD')]);

export const PlaceOrderPayloadSchema = Type.Object({
  symbol: Type.String({ minLength: 1, maxLength: 32 }),
  side: SideSchema,
  type: OrderTypeSchema,
  quantity: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
  limitPrice: NullableNumberSchema,
  tif: TifSchema,
  goodTillTime: NullableNumberSchema
});

export const ClientCommandPayloadSchema = Type.Union([
  Type.Object({ type: Type.Literal('PLACE_ORDER'), order: PlaceOrderPayloadSchema }),
  Type.Object({ type: Type.Literal('REPLACE_ORDER'), orderId: Type.String({ minLength: 1, maxLength: 128 }), quantity: Type.Integer({ minimum: 1, maximum: 1_000_000 }), limitPrice: NullableNumberSchema }),
  Type.Object({ type: Type.Literal('CANCEL_ORDER'), orderId: Type.String({ minLength: 1, maxLength: 128 }) }),
  Type.Object({ type: Type.Literal('SET_SPEED'), speed: Type.Union([Type.Literal(1), Type.Literal(2)]) }),
  Type.Object({ type: Type.Literal('PAUSE') }),
  Type.Object({ type: Type.Literal('RESUME') }),
  Type.Object({ type: Type.Literal('FINISH_M2') })
]);

export const ClientCommandEnvelopeSchema = Type.Object({
  protocolVersion: Type.Literal(PROTOCOL_VERSION),
  commandId: Type.String({ minLength: 1, maxLength: 128 }),
  attemptId: Type.String({ minLength: 1, maxLength: 64 }),
  clientSentAt: Type.String({ minLength: 1, maxLength: 64 }),
  payload: ClientCommandPayloadSchema
});

export interface TimelineEventConfig {
  readonly id: string;
  readonly at: number;
  readonly kind: 'MESSAGE' | 'OBJECTIVE' | 'CHECKPOINT' | 'CONTROL' | 'MARKET_EVENT' | 'END';
  readonly payload: Readonly<Record<string, unknown>>;
}

export interface ScenarioConfig {
  readonly missionId: MissionId;
  readonly scenarioVersion: string;
  readonly engineVersion: string;
  readonly scoreVersion: string;
  readonly title: string;
  readonly hook: string;
  readonly durationLabel: string;
  readonly skills: readonly string[];
  readonly recommended: boolean;
  readonly deterministicSeed: number;
  readonly exogenousPathId: string;
  readonly startAt: number;
  readonly endAt: number;
  readonly sessionDate: string;
  readonly initialCash: number;
  readonly initialPositions: Readonly<Record<string, number>>;
  readonly defaultWatchlist: readonly string[];
  readonly availableSymbols: readonly string[];
  readonly contracts: Readonly<Record<string, ContractSpec>>;
  readonly initialPrices: Readonly<Record<string, number>>;
  readonly calibration: Readonly<Record<string, MarketCalibration>>;
  readonly timeline: readonly TimelineEventConfig[];
  readonly brief: MissionBrief;
}

export interface MarketCalibration {
  readonly baseSpreadTicks: number;
  readonly visibleDepth: number;
  readonly deepDepth: number;
  readonly volatilityTicks: number;
  readonly replenishRate: number;
  readonly impactScale: number;
  readonly toxicityDecay: number;
  readonly criticalToxicity: number;
  readonly majorToxicity: number;
}

export function isTerminalStatus(status: AttemptStatus): boolean {
  return status === 'COMPLETED' || status === 'ABORTED' || status === 'INCIDENT' || status === 'INSOLVENT';
}

export function scoreLabel(score: number): string {
  if (score === 0) return 'Échec critique';
  if (score <= 39) return 'Mandat non atteint';
  if (score <= 59) return 'Partiel';
  if (score <= 79) return 'Mandat atteint';
  if (score <= 89) return 'Très bon';
  return 'Remarquable';
}
